// File: RecordOvertimePaymentPage.js
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";

// Icons
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Custom components
import CustomAlert from "../../components/CustomAlert";

// API service
import { recordOvertimeAdminApi } from "../../api/overtimeAndAdvanceApi";
import Configs from "../../configs/Configs";

const DEFAULT_AVATAR = "https://www.gravatar.com/avatar/?d=mp&s=80";

function RecordOvertimePaymentPage() {
  const { state } = useLocation();
  const user_id = state?.user_id;
  const photo = state?.photo;
  const fullName = state?.full_name || "";

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [hours, setHours] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (msg, severity = "info") => {
    setAlertMessage(msg);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  useEffect(() => {
    if (!user_id) {
      showAlert("No user selected. Go back and select a user.", "error");
    }
  }, [user_id]);

  const validateFields = () => {
    const newErrors = {};

    if (!hours || isNaN(hours) || Number(hours) <= 0) {
      newErrors.hours = "Please enter valid hours.";
    }

    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount.";
    }

    if (!remarks || remarks.trim() === "") {
      newErrors.remarks = "Remarks are required.";
    }

    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      newErrors.date = "Cannot record overtime for future months.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!user_id) return;
    if (!validateFields()) return;

    setLoading(true);
    try {
      const payload = {
        user_id,
        hours: Number(hours),
        amount: Number(amount),
        remarks: remarks.trim(),
        month,
        year,
      };

      const res = await recordOvertimeAdminApi(payload);

      if (res.ok && res.data?.status === "success") {
        showAlert("Overtime recorded successfully!", "success");
        setHours("");
        setAmount("");
        setRemarks("");
      } else {
        showAlert(res.data?.message || "Failed to record overtime.", "error");
      }
    } catch (err) {
      console.error(err);
      showAlert("Server error. Could not save overtime.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox sx={{ maxWidth: "600px", margin: "0 auto 0 0" }}>
          {/* User Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
              <Avatar
                src={photo ? `${Configs.baseUrl}${photo}` : DEFAULT_AVATAR}
                sx={{ width: 80, height: 80 }}
              />
              <MDBox flexGrow={1} minWidth={200}>
                <MDTypography variant="h5" fontWeight="bold" mb={1}>
                  Overtime for {fullName}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Enter overtime details for the selected user
                </MDTypography>
              </MDBox>
            </MDBox>
          </Paper>

          {/* Form */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: "lg" }}>
            <MDBox mb={2}>
              <Grid container spacing={3}>
                {/* Hours */}
                <Grid item xs={12}>
                  <TextField
                    label={
                      <MDBox display="flex" alignItems="center">
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                        Hours (Required)
                      </MDBox>
                    }
                    fullWidth
                    type="number"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    error={!!errors.hours}
                    helperText={errors.hours}
                    sx={{ "& .MuiOutlinedInput-root": { borderColor: errors.hours ? "red" : "" } }}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12}>
                  <TextField
                    label={
                      <MDBox display="flex" alignItems="center">
                        <PaymentsIcon fontSize="small" sx={{ mr: 1 }} />
                        Lumpsum Amount (KES) (Required)
                      </MDBox>
                    }
                    fullWidth
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    error={!!errors.amount}
                    helperText={errors.amount}
                    sx={{ "& .MuiOutlinedInput-root": { borderColor: errors.amount ? "red" : "" } }}
                  />
                </Grid>

                {/* Remarks */}
                <Grid item xs={12}>
                  <TextField
                    label={
                      <MDBox display="flex" alignItems="center">
                        <EventNoteIcon fontSize="small" sx={{ mr: 1 }} />
                        Remarks (Required)
                      </MDBox>
                    }
                    fullWidth
                    multiline
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    error={!!errors.remarks}
                    helperText={errors.remarks}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderColor: errors.remarks ? "red" : "" },
                    }}
                  />
                </Grid>

                {/* Month */}
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Month"
                    fullWidth
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 48,
                        paddingTop: 1,
                        paddingBottom: 1,
                        borderColor: errors.date ? "red" : "",
                      },
                    }}
                  >
                    <MenuItem value={currentMonth}>{currentMonth}</MenuItem>
                  </TextField>
                  {errors.date && (
                    <MDTypography variant="caption" color="error">
                      {errors.date}
                    </MDTypography>
                  )}
                </Grid>

                {/* Year */}
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Year"
                    fullWidth
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 48,
                        paddingTop: 1,
                        paddingBottom: 1,
                        borderColor: errors.date ? "red" : "",
                      },
                    }}
                  >
                    <MenuItem value={currentYear}>{currentYear}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </MDBox>

            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={loading}
              onClick={handleSave}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Record Overtime"}
            </MDButton>
          </Paper>
        </MDBox>
      </MDBox>

      <CustomAlert
        message={alertMessage}
        severity={alertSeverity}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default RecordOvertimePaymentPage;
